import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BRANCH_CODES,
  BRANCH_LABELS,
  HUB_BRANCH,
  REQUEST_KINDS,
  URGENCY_LEVELS,
  type BranchCode,
  type RequestKind,
  type Urgency,
} from '@lorsamthai/ops-shared';
import { useAuth } from '../auth/context.js';
import { createBranchRequest } from '../lib/queries.js';
import {
  REQUEST_KIND_ICONS,
  REQUEST_KIND_LABELS,
  URGENCY_LABELS,
} from '../lib/requestLabels.js';

interface LineRow {
  sku: string;
  qty: number;
  note: string;
}

const KIND_HELP: Record<RequestKind, string> = {
  transfer_request: 'ขอให้สาขาอื่น (ปกติ บางพลี) โอนยางมาเติม — อนุมัติแล้วระบบสร้างใบโอนให้',
  purchase_request: 'ขอให้ super สั่งซื้อจาก supplier — อนุมัติแล้วระบบรวม PO ตาม supplier',
  stock_adjust: 'แจ้งว่ายอด stock ในระบบไม่ตรง — super ตรวจและปรับให้ที่หน้า SKU',
  target_change: 'ขอปรับ min / target ของ SKU — super ตรวจและแก้ที่หน้า /stock/:sku',
  demand_request: 'แจ้ง demand จากลูกค้า / ตลาด — feed เข้า velocity เพื่อให้ระบบสั่งล่วงหน้า',
};

export default function RequestNew() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const isSuper = me?.role === 'super_admin';
  const userBranch = me?.branch ?? null;

  const [kind, setKind] = useState<RequestKind>(
    (sp.get('kind') as RequestKind) || 'transfer_request'
  );
  const [fromBranch, setFromBranch] = useState<BranchCode>(userBranch ?? 'bp');
  const [sourceBranch, setSourceBranch] = useState<BranchCode>(HUB_BRANCH);
  const [urgency, setUrgency] = useState<Urgency>('normal');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<LineRow[]>([
    { sku: sp.get('sku') ?? '', qty: 4, note: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const requiresSource = kind === 'transfer_request';

  const sourceOptions = useMemo(
    () => BRANCH_CODES.filter((b) => b !== fromBranch),
    [fromBranch]
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const items = lines
      .map((l) => ({ sku: l.sku.trim(), qty: l.qty, note: l.note.trim() || undefined }))
      .filter((l) => l.sku && l.qty > 0);
    if (items.length === 0) {
      setError('ต้องมีอย่างน้อย 1 รายการ (SKU + จำนวน)');
      return;
    }
    if (requiresSource && sourceBranch === fromBranch) {
      setError('สาขาต้นทางและปลายทางต้องต่างกัน');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createBranchRequest({
        kind,
        urgency,
        reason: reason.trim() || undefined,
        items,
        source_branch: requiresSource ? sourceBranch : undefined,
        ...(isSuper ? { from_branch: fromBranch } : {}),
      });
      navigate(`/requests/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const setLine = (idx: number, patch: Partial<LineRow>) =>
    setLines((p) => p.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  return (
    <div className="space-y-5">
      <div>
        <Link to="/requests" className="text-xs text-slate-500 hover:underline">
          ← กลับรายการคำขอ
        </Link>
        <h1 className="mt-1 text-xl font-bold">สร้างคำขอใหม่</h1>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {/* Kind picker */}
        <fieldset className="rounded-xl border border-slate-200 bg-white p-4">
          <legend className="px-1 text-sm font-medium">ประเภทคำขอ</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {REQUEST_KINDS.map((k) => (
              <label
                key={k}
                className={
                  'flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ' +
                  (kind === k
                    ? 'border-slate-900 bg-slate-50'
                    : 'border-slate-200 hover:bg-slate-50')
                }
              >
                <input
                  type="radio"
                  name="kind"
                  value={k}
                  checked={kind === k}
                  onChange={() => setKind(k)}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="font-medium">
                    {REQUEST_KIND_ICONS[k]} {REQUEST_KIND_LABELS[k]}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">{KIND_HELP[k]}</div>
                </div>
              </label>
            ))}
          </div>
        </fieldset>

        {/* Branch + urgency */}
        <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3">
          {isSuper ? (
            <label className="space-y-1 text-sm">
              <span className="block font-medium">สาขาที่ขอ (from_branch)</span>
              <select
                value={fromBranch}
                onChange={(e) => setFromBranch(e.target.value as BranchCode)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
              >
                {BRANCH_CODES.map((b) => (
                  <option key={b} value={b}>
                    {BRANCH_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="text-sm">
              <div className="font-medium">สาขาที่ขอ</div>
              <div className="mt-1 rounded-md bg-slate-50 px-3 py-1.5 text-slate-600">
                {userBranch ? BRANCH_LABELS[userBranch] : '—'}
              </div>
            </div>
          )}

          {requiresSource && (
            <label className="space-y-1 text-sm">
              <span className="block font-medium">ขอจากสาขา</span>
              <select
                value={sourceBranch}
                onChange={(e) => setSourceBranch(e.target.value as BranchCode)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
              >
                {sourceOptions.map((b) => (
                  <option key={b} value={b}>
                    {BRANCH_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="space-y-1 text-sm">
            <span className="block font-medium">ความเร่งด่วน</span>
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as Urgency)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
            >
              {URGENCY_LEVELS.map((u) => (
                <option key={u} value={u}>
                  {URGENCY_LABELS[u]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Reason */}
        <label className="block rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <span className="block font-medium">เหตุผล / โน้ต</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder={
              kind === 'demand_request'
                ? 'เช่น ลูกค้าอู่ A ถามไซส์นี้ทุกอาทิตย์ — น่าจะ stock เพิ่ม'
                : kind === 'stock_adjust'
                ? 'เช่น นับ stock จริง 3 เส้น แต่ระบบขึ้น 5'
                : 'อธิบายเหตุผลของคำขอ'
            }
            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2"
          />
        </label>

        {/* Items */}
        <fieldset className="rounded-xl border border-slate-200 bg-white p-4">
          <legend className="px-1 text-sm font-medium">รายการ</legend>
          <ul className="space-y-2">
            {lines.map((l, idx) => (
              <li key={idx} className="grid grid-cols-[1fr_80px_1fr_auto] gap-2 text-sm">
                <input
                  value={l.sku}
                  onChange={(e) => setLine(idx, { sku: e.target.value })}
                  placeholder="SKU"
                  className="rounded-md border border-slate-200 px-2 py-1 font-mono"
                />
                <input
                  type="number"
                  min={1}
                  value={l.qty}
                  onChange={(e) => setLine(idx, { qty: Number(e.target.value) })}
                  className="rounded-md border border-slate-200 px-2 py-1 text-right"
                />
                <input
                  value={l.note}
                  onChange={(e) => setLine(idx, { note: e.target.value })}
                  placeholder="โน้ต (ไม่บังคับ)"
                  className="rounded-md border border-slate-200 px-2 py-1"
                />
                <button
                  type="button"
                  disabled={lines.length === 1}
                  onClick={() => setLines((p) => p.filter((_, i) => i !== idx))}
                  className="rounded-md border border-slate-200 px-2 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                >
                  ลบ
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setLines((p) => [...p, { sku: '', qty: 4, note: '' }])}
            className="mt-3 rounded-md border border-slate-200 px-3 py-1 text-xs hover:bg-slate-50"
          >
            + เพิ่มแถว
          </button>
        </fieldset>

        {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <Link
            to="/requests"
            className="rounded-md border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50"
          >
            ยกเลิก
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {submitting ? 'กำลังส่ง...' : 'ส่งคำขอ'}
          </button>
        </div>
      </form>
    </div>
  );
}
